import type { ChatMessage, Conversation, MessageContent, StoredChatState } from '../types/chat';

export const CHAT_STORAGE_KEY = 'workers-ai-chat-state-v2';
const LEGACY_HISTORY_KEY = 'kimi-chat-history';

export function createId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function emptyConversation(now = Date.now()): Conversation {
  return { id: createId(), title: 'Percakapan baru', messages: [], createdAt: now, updatedAt: now };
}

function isContent(value: unknown): value is MessageContent {
  return typeof value === 'string' || Array.isArray(value);
}

function migrateLegacy(): StoredChatState | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(LEGACY_HISTORY_KEY) || '[]') as Array<{
      role?: unknown;
      content?: unknown;
    }>;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const now = Date.now();
    const messages: ChatMessage[] = parsed
      .filter((item) => (item.role === 'user' || item.role === 'assistant') && isContent(item.content))
      .map((item, index) => ({
        id: createId(),
        role: item.role as 'user' | 'assistant',
        content: item.content as MessageContent,
        createdAt: now + index,
        status: 'complete',
      }));
    if (!messages.length) return null;
    const conversation: Conversation = {
      id: createId(),
      title: 'Riwayat sebelumnya',
      messages,
      createdAt: now,
      updatedAt: now,
    };
    return { version: 2, activeId: conversation.id, conversations: [conversation] };
  } catch {
    return null;
  }
}

export function loadChatState(): StoredChatState {
  try {
    const parsed = JSON.parse(localStorage.getItem(CHAT_STORAGE_KEY) || 'null') as StoredChatState | null;
    if (parsed?.version === 2 && Array.isArray(parsed.conversations) && parsed.conversations.length) {
      const activeId = parsed.conversations.some((item) => item.id === parsed.activeId)
        ? parsed.activeId
        : parsed.conversations[0].id;
      return { ...parsed, activeId };
    }
  } catch {
    // Continue with migration or a clean local conversation.
  }

  const migrated = migrateLegacy();
  if (migrated) return migrated;
  const conversation = emptyConversation();
  return { version: 2, activeId: conversation.id, conversations: [conversation] };
}

export function persistChatState(state: StoredChatState): void {
  localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(state));
}
